import { Dayjs } from "dayjs";
import { ExpressContact } from "../../types/express-master.type";
import {
  ExpressCreditBill,
  ExpressDocumentRequest,
} from "../../types/express-document.type";
import { formatNumber } from "../../utils";
import { API_DATE_FORMAT } from "../SpecialOrder/data";
import { ExpressDocTypeConfig, hasPriceType } from "./constants";
import {
  BillLineDraft,
  DocumentDraft,
  LineDraft,
  PaymentLineDraft,
  billLineFromCreditBill,
  draftBillPayTotal,
  draftPaymentTotals,
  draftTotals,
  partyFromContact,
  toNumber,
} from "./documentDraft";

/**
 * The rules over a draft: the defaults a pick fills in, what the draft must hold before it is
 * sent (in the server's words where it has them), and how it maps onto the request body.
 */

export type DocumentFormErrorKey =
  | "branch"
  | "party"
  | "docDate"
  | "paymentDate"
  | "supplierBillNo"
  | "supplierBillDate"
  | "taxPeriod"
  | "discountAmount"
  | "lines"
  | "billLines"
  | "paymentLines"
  | "received";

export type DocumentFormErrors = Partial<Record<DocumentFormErrorKey, string>>;

const TAX_PERIOD_FORMAT = "YYYY-MM";

/** Picking a contact takes its details; a ซื้อเชื่อ also starts from the contact's credit term. */
export const withContact = (
  draft: DocumentDraft,
  contact: ExpressContact | null,
  config: ExpressDocTypeConfig,
): DocumentDraft => ({
  ...draft,
  party: contact ? partyFromContact(contact) : null,
  creditDays: config.hasCreditTerm && contact ? String(contact.credit_days) : draft.creditDays,
});

/** ยื่นงวด follows the month of the supplier's bill date until the keyer picks another. */
export const withSupplierBillDate = (draft: DocumentDraft, date: Dayjs | null): DocumentDraft => ({
  ...draft,
  supplierBillDate: date,
  taxPeriod: date?.isValid() ? date.startOf("month") : draft.taxPeriod,
});

/** Adds the picked credit bills; the receipt's salesperson defaults to the first bill's. */
export const withCreditBills = (draft: DocumentDraft, bills: ExpressCreditBill[]): DocumentDraft => {
  const added = bills
    .filter((bill) => !draft.billLines.some((line) => line.billNo === bill.bill_no))
    .map(billLineFromCreditBill);
  const billLines = [...draft.billLines, ...added];
  const firstSalesperson = billLines.find((line) => line.salespersonCode)?.salespersonCode ?? "";
  return {
    ...draft,
    billLines,
    salespersonCode: draft.salespersonCode || firstSalesperson,
  };
};

const lineError = (line: LineDraft, config: ExpressDocTypeConfig): string | undefined => {
  if (!line.account) return "กรุณาเลือกรหัสบัญชีให้ครบทุกบรรทัด";
  if (!line.description.trim()) return "กรุณากรอกรายละเอียดให้ครบทุกบรรทัด";
  if (config.lineMode === "AMOUNT") {
    return toNumber(line.amount) > 0 ? undefined : "กรุณากรอกจำนวนเงินให้ครบทุกบรรทัด";
  }
  if (toNumber(line.qty) <= 0) return "กรุณากรอกจำนวนให้ครบทุกบรรทัด";
  return toNumber(line.unitPrice) === 0 ? "กรุณากรอกราคาต่อหน่วยให้ครบทุกบรรทัด" : undefined;
};

const billLineError = (line: BillLineDraft): string | undefined => {
  const payAmount = toNumber(line.payAmount);
  if (payAmount <= 0) return "กรุณากรอกยอดชำระให้ครบทุกบิล";
  return payAmount > line.outstandingAmount
    ? `ยอดชำระของบิล ${line.billNo} เกินยอดค้างชำระ ${formatNumber(line.outstandingAmount)} บาท`
    : undefined;
};

const paymentLineError = (line: PaymentLineDraft): string | undefined => {
  if (!line.account) return "กรุณาเลือกรหัสบัญชีของรายการชำระโดยอื่นๆ ให้ครบ";
  return toNumber(line.amount) > 0 ? undefined : "กรุณากรอกจำนวนเงินของรายการชำระโดยอื่นๆ ให้ครบ";
};

const linesErrors = (draft: DocumentDraft, config: ExpressDocTypeConfig): DocumentFormErrors => {
  if (config.lineMode === "BILLS") {
    if (draft.billLines.length === 0) return { billLines: "กรุณาเลือกบิลที่รับชำระอย่างน้อย 1 บิล" };
    const billError = draft.billLines.map(billLineError).find(Boolean);
    return billError ? { billLines: billError } : {};
  }
  if (draft.lines.length === 0) return { lines: "กรุณาเพิ่มรายการอย่างน้อย 1 บรรทัด" };
  const firstLineError = draft.lines.map((line) => lineError(line, config)).find(Boolean);
  return firstLineError ? { lines: firstLineError } : {};
};

/** With VAT, the ภาษีซื้อ report needs the supplier's bill number, its date and the tax month. */
const supplierBillErrors = (draft: DocumentDraft): DocumentFormErrors => {
  if (draft.priceType === 0) return {};
  const errors: DocumentFormErrors = {};
  if (!draft.supplierBillNo.trim()) errors.supplierBillNo = "กรุณากรอกเลขที่บิล";
  if (!draft.supplierBillDate?.isValid()) errors.supplierBillDate = "กรุณาเลือกวันที่บิล";
  if (!draft.taxPeriod?.isValid()) errors.taxPeriod = "กรุณาเลือกงวดภาษี";
  return errors;
};

/** ยอดชำระจริง has to settle the document: its total (O1) or what it pays on the bills (E1). */
const paymentErrors = (
  draft: DocumentDraft,
  config: ExpressDocTypeConfig,
  vatRate: number,
): DocumentFormErrors => {
  const paymentLineMessage = draft.paymentLines.map(paymentLineError).find(Boolean);
  if (paymentLineMessage) return { paymentLines: paymentLineMessage };
  const { receivedAmount } = draftPaymentTotals(draft);
  const isReceipt = config.lineMode === "BILLS";
  const due = isReceipt ? draftBillPayTotal(draft) : draftTotals(draft, config, vatRate).netAmount;
  if (receivedAmount === due) return {};
  const dueLabel = isReceipt ? "ยอดชำระตามใบกำกับ" : "จำนวนเงินรวมทั้งสิ้น";
  return {
    received: `ยอดชำระจริง ${formatNumber(receivedAmount)} บาท ต้องเท่ากับ${dueLabel} ${formatNumber(due)} บาท`,
  };
};

export interface DocumentValidationContext {
  /** A cross-branch role keying a new document names its branch. */
  requiresBranch: boolean;
  vatRate: number;
}

export function validateDocumentDraft(
  draft: DocumentDraft,
  config: ExpressDocTypeConfig,
  { requiresBranch, vatRate }: DocumentValidationContext,
): DocumentFormErrors {
  const errors: DocumentFormErrors = {};
  if (requiresBranch && !draft.branchUuid) errors.branch = "กรุณาเลือกสาขา";
  if (!draft.party) errors.party = `กรุณาเลือก${config.partyLabel}`;
  if (!draft.docDate?.isValid()) errors.docDate = `กรุณาเลือก${config.docDateLabel}`;
  if (config.lineMode === "BILLS" && !draft.paymentDate?.isValid()) {
    errors.paymentDate = "กรุณาเลือกวันที่รับชำระ";
  }
  if (config.hasSupplierBill) Object.assign(errors, supplierBillErrors(draft));
  if (config.hasDiscount) {
    const { subtotal, discountAmount } = draftTotals(draft, config, vatRate);
    if (discountAmount < 0 || discountAmount > subtotal) {
      errors.discountAmount = "ส่วนลดต้องไม่เกินจำนวนเงิน";
    }
  }
  Object.assign(errors, linesErrors(draft, config));
  if (config.hasPayment) Object.assign(errors, paymentErrors(draft, config, vatRate));
  return errors;
}

/** Optional text: trimmed, and null when blank so a cleared field is cleared on an edit. */
const optionalText = (text: string): string | null => text.trim() || null;

const optionalDate = (date: Dayjs | null, format = API_DATE_FORMAT): string | null =>
  date?.isValid() ? date.format(format) : null;

type RequestPart = Partial<ExpressDocumentRequest>;

const partyFields = (draft: DocumentDraft, config: ExpressDocTypeConfig): RequestPart =>
  config.contactKind
    ? { express_contact_uuid: draft.party?.contactUuid ?? undefined }
    : {
        customer_code: draft.party?.code,
        reference: optionalText(draft.reference),
        salesperson_code: optionalText(draft.salespersonCode),
        payment_date: optionalDate(draft.paymentDate) ?? undefined,
      };

const priceFields = (draft: DocumentDraft, config: ExpressDocTypeConfig): RequestPart => {
  if (!hasPriceType(config)) return {};
  return {
    price_type: draft.priceType,
    discount_amount: config.hasDiscount ? toNumber(draft.discountAmount) : undefined,
  };
};

const supplierBillFields = (draft: DocumentDraft, config: ExpressDocTypeConfig): RequestPart => {
  if (!config.hasSupplierBill) return {};
  return {
    supplier_bill_no: optionalText(draft.supplierBillNo),
    supplier_bill_date: optionalDate(draft.supplierBillDate),
    tax_period: optionalDate(draft.taxPeriod, TAX_PERIOD_FORMAT),
  };
};

const paymentFields = (draft: DocumentDraft, config: ExpressDocTypeConfig): RequestPart => {
  if (!config.hasPayment) return {};
  return {
    cash_amount: toNumber(draft.cashAmount),
    wht_amount: toNumber(draft.whtAmount),
    payment_lines: draft.paymentLines.map((line) => ({
      account_code: line.account?.code ?? "",
      cheque_date: optionalDate(line.chequeDate),
      amount: toNumber(line.amount),
    })),
  };
};

const lineFields = (draft: DocumentDraft, config: ExpressDocTypeConfig): RequestPart => {
  if (config.lineMode === "BILLS") {
    return {
      bill_lines: draft.billLines.map((line) => ({
        bill_no: line.billNo,
        pay_amount: toNumber(line.payAmount),
      })),
    };
  }
  return {
    lines: draft.lines.map((line) => {
      const shared = { account_code: line.account?.code ?? "", description: line.description.trim() };
      if (config.lineMode === "AMOUNT") return { ...shared, amount: toNumber(line.amount) };
      return {
        ...shared,
        warehouse_code: optionalText(line.warehouseCode),
        qty: toNumber(line.qty),
        unit_price: toNumber(line.unitPrice),
      };
    }),
  };
};

/**
 * The body for a draft that passed `validateDocumentDraft`, for both the create and the edit.
 * Each type sends only its own fields; no total is sent, since the server works them all out.
 */
export const buildDocumentRequest = (
  draft: DocumentDraft,
  config: ExpressDocTypeConfig,
  branchCode: string | undefined,
): ExpressDocumentRequest => ({
  doc_type: config.docType,
  branch_code: branchCode,
  doc_date: optionalDate(draft.docDate) ?? "",
  remark: optionalText(draft.remark),
  ...partyFields(draft, config),
  ...priceFields(draft, config),
  ...supplierBillFields(draft, config),
  credit_days: config.hasCreditTerm ? toNumber(draft.creditDays) : undefined,
  ...paymentFields(draft, config),
  ...lineFields(draft, config),
});
